import "../styles/gradient-button.min.css"
import { v4 as uuidv4 } from "uuid"
import { useState, forwardRef, useEffect, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { setRefreshKey } from "state"
import { IoClose } from "react-icons/io5"
import { TbChartCircles } from "react-icons/tb";
import { FaCircle, FaRegCircle } from "react-icons/fa";
import { IoMdAdd, IoMdClose } from "react-icons/io";
import { IoLanguageSharp, IoRefresh } from "react-icons/io5";
import { PiSparkleFill, PiSparkle, PiChatCircle, PiChatCircleDots, PiChatCircleText, PiBookOpen, PiBookOpenText } from "react-icons/pi";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query"
import HashLoader from "react-spinners/HashLoader"
import {
  Box,
  Stack,
  Button,
  Checkbox,
  Collapse,
  Tooltip,
  Dialog,
  Slide,
  Snackbar,
  InputBase,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material"
import { pinyin } from "pinyin-pro"
import apiUrl from "config/api"

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

/* Smoothly animates a field's height on both entrance and removal.
   Stays mounted through the exit transition, then unmounts so no empty gap remains. */
const CollapseField = ({ open, children }) => {
  const [mounted, setMounted] = useState(open)
  useEffect(() => {
    if (open) setMounted(true)
  }, [open])
  if (!mounted) return null
  return (
    <Collapse
      in={open}
      appear
      timeout={350}
      easing={"cubic-bezier(0.4, 0, 0.2, 1)"}
      onExited={() => setMounted(false)}
    >
      {children}
    </Collapse>
  )
}

export default function AddVocabDialog({ text }) {
  const isNonMobileScreens = useMediaQuery("(min-width:1000px) and (max-height:2160px)")
  const isQHDScreens = useMediaQuery("(min-width:2500px) and (max-height:1600px)") // 2K Laptops
  const isWideScreens = useMediaQuery("(min-width:3400px) and (max-height:1500px)") // Wide and Ultrawide Desktops
  const isSmallMobileScreens = useMediaQuery("(max-width:800px) and (max-height:800px)")
  const isFullHDScreens = useMediaQuery("(min-width:1800px) and (max-height:2160px)")
  const theme = useTheme()
  const mode = useSelector((state) => state.mode)
  const { _id } = useSelector((state) => state.user)
  const token = useSelector((state) => state.token)
  const refreshKey = useSelector((state) => state.refreshKey)
  const queryClient = useQueryClient()
  const dispatch = useDispatch()

  /* Typewriter effect for AI-generated text */
  const typingTimersRef = useRef([])

  useEffect(() => {
    const timers = typingTimersRef.current
    return () => {
      timers.forEach((timer) => clearInterval(timer))
    }
  }, [])

  const typeWriter = (setter, fullText, speed = 15) => {
    if (typeof fullText !== "string" || fullText.length === 0) {
      setter(fullText || "")
      return
    }
    setter("")
    let index = 0
    const timer = setInterval(() => {
      index += 1
      setter(fullText.slice(0, index))
      if (index >= fullText.length) {
        clearInterval(timer)
        typingTimersRef.current = typingTimersRef.current.filter((t) => t !== timer)
      }
    }, speed)
    typingTimersRef.current.push(timer)
  }

  const isLandscape = window.matchMedia("(orientation: landscape)").matches;
  const isPortrait = window.matchMedia("(orientation: portrait)").matches;

  const [openDialog, setOpenDialog] = useState(false);

  const handleDialogOpen = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    dispatch(setRefreshKey({ refreshKey: refreshKey + 1 }))
    setOpenDialog(false);
  };

  /* Create Vocab State */
  const [vocabText, setVocabText] = useState("")
  const [requireVocabText, setRequireVocabText] = useState(false)

  const [vocabPinyin, setVocabPinyin] = useState("")
  const convertToPinyin = (chinese) => {
    return pinyin(chinese)
  }

  // Debounce pinyin generation so the IME has time to commit the full
  // Chinese character before converting, ensuring correct tone marks.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setVocabPinyin(convertToPinyin(vocabText))
    }, 400)
    return () => clearTimeout(timeoutId)
  }, [vocabText])

  const [vocabLevel, setVocabLevel] = useState("1")
  const [vocabLevelHover, setVocabLevelHover] = useState("1")

  /* Get Label Data for Edit */
  const getVocabLabels = () => {
    return fetch(`${apiUrl}/labels/${_id}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
  }

  const { data: labelsData } = useQuery(["vocabLabelsData"], getVocabLabels, {
    keepPreviousData: true,
    staleTime: 500
  })

  /* Labels Input States */
  const [checkedLabels, setCheckedLabels] = useState([]);
  const [showLabels, setShowLabels] = useState(false)

  // Handle check/uncheck actions
  const handleCheckboxChange = (text) => {
    setCheckedLabels((prevCheckedLabels) =>
      prevCheckedLabels.includes(text)
        ? prevCheckedLabels.filter((label) => label !== text) // Uncheck (remove from array)
        : [...prevCheckedLabels, text] // Check (add to array)
    );
  }

  const [vocabDefinition, setVocabDefinition] = useState("")
  const [generatingDef, setGeneratingDef] = useState(false)
  const [showRegenDef, setShowRegenDef] = useState(false)
  const [generatingDetailedDef, setGeneratingDetailedDef] = useState(false)

  /* Generate Ai Definition Data */
  const getGenDef = () => {
    const subUrl = generatingDetailedDef ? "detaildefinition" : "definition"
    return fetch(`${apiUrl}/openai/${subUrl}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json", // Ensure Content-Type is set to JSON
      },
      body: JSON.stringify({ chineseVocab: vocabText, vocabLabel: checkedLabels }), // Send the correct data in the body
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Network response was not ok');
        }
        return res.json(); // Parse the response as JSON
      });
  };

  const { data: genDefData } = useQuery(["aiDefData", vocabText], getGenDef,
    {
      enabled: !!vocabText && generatingDef, // Ensure that vocabText is not empty
      keepPreviousData: true,
    }
  );

  const requestGenDef = useMutation({
    mutationFn: async () => {
      const subUrl = generatingDetailedDef ? "detaildefinition" : "definition"
      return await fetch(`${apiUrl}/openai/${subUrl}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          chineseVocab: vocabText,
          vocabLabel: checkedLabels
        }),
      }).then((res) => res.json()); // Ensure the response is parsed as JSON
    },
    onError: (error, _styleName, context) => {
      console.log("Error fetching:" + context?.id + error);
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({ queryKey: ["aiDefData"] });
      queryClient.invalidateQueries({ queryKey: ["aiGrammarData"] });

      if (data?.definition) {
        typeWriter(setVocabDefinition, data.definition.toLowerCase());
      }

      setGeneratingDef(false)
    },
  });

  const handleRequestGenDef = () => {
    setGeneratingDef(true)
    if (vocabText.length < 1) {
      setRequireVocabText(true)
      return
    }
    requestGenDef.mutate()
  }

  const [vocabColloquial, setVocabColloquial] = useState("")
  const [generatingColloquial, setGeneratingColloquial] = useState(false)
  const [showRegenColloquial, setShowRegenColloquial] = useState(false)
  const [multilineColloquial, setMultilineColloquial] = useState(false)


  /* Generate Ai Colloquial Data */
  const getGenColloquial = () => {
    return fetch(`${apiUrl}/openai/colloquial`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json", // Ensure Content-Type is set to JSON
      },
      body: JSON.stringify({ chineseVocab: vocabText }), // Send the correct data in the body
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Network response was not ok');
        }
        return res.json(); // Parse the response as JSON
      });
  };

  const { data: genColloquialData } = useQuery(["aiColloquialData", vocabText], getGenColloquial,
    {
      enabled: !!vocabText && generatingColloquial, // Ensure that vocabText is not empty
      keepPreviousData: true,
    }
  );

  const requestGenColloquial = useMutation({
    mutationFn: async () => {
      return await fetch(`${apiUrl}/openai/colloquial`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          chineseVocab: vocabText,
        }),
      }).then((res) => res.json()); // Ensure the response is parsed as JSON
    },
    onError: (error, _styleName, context) => {
      console.log("Error fetching:" + context?.id + error);
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({ queryKey: ["aiColloquialData"] });

      if (data?.colloquial) {
        typeWriter(setVocabColloquial, data.colloquial);
      }

      setGeneratingColloquial(false)
    },
  });

  const handleRequestGenColloquial = () => {
    setGeneratingColloquial(true)
    if (vocabText.length < 1) {
      setRequireVocabText(true)
      return
    }
    requestGenColloquial.mutate()
  }

  /* Generate Ai Grammar Data */
  const getGenGrammar = () => {
    return fetch(`${apiUrl}/openai/grammar`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json", // Ensure Content-Type is set to JSON
      },
      body: JSON.stringify({ chineseVocab: vocabText }), // Send the correct data in the body
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Network response was not ok');
        }
        return res.json(); // Parse the response as JSON
      });
  };

  const { data: genGrammarData } = useQuery(["aiGrammarData", vocabText], getGenGrammar,
    {
      enabled: !!vocabText && generatingDef, // Ensure that vocabText is not empty
      keepPreviousData: true,
    }
  );

  const requestGenGrammar = useMutation({
    mutationFn: async () => {
      return await fetch(`${apiUrl}/openai/grammar`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          chineseVocab: vocabText,
        }),
      }).then((res) => res.json()); // Ensure the response is parsed as JSON
    },
    onError: (error, _styleName, context) => {
      console.log("Error fetching:" + context?.id + error);
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({ queryKey: ["aiGrammarData"] });
      queryClient.invalidateQueries({ queryKey: ["aiDefData"] });

      if (data?.explanation) {
        typeWriter(setVocabDefinition, data.explanation.toLowerCase());
      }

      setGeneratingDef(false)
    },
  });

  const handleRequestGenGrammar = () => {
    setGeneratingDef(true)
    if (vocabText.length < 1) {
      setRequireVocabText(true)
      return
    }
    requestGenGrammar.mutate()
  }

  const [vocabSimilarOne, setVocabSimilarOne] = useState("")
  const [vocabSimilarTwo, setVocabSimilarTwo] = useState("")
  const [vocabSimilarThree, setVocabSimilarThree] = useState("")
  const [vocabSimilarFour, setVocabSimilarFour] = useState("")
  const [vocabSimilar, setVocabSimilar] = useState([]) //max 4
  const [vocabSimilarShowCount, setVocabSimilarShowCount] = useState(1)
  const incrementVocabSimilarCount = () => {
    if (vocabSimilarShowCount >= 4) return
    setVocabSimilarShowCount(vocabSimilarShowCount + 1)
  }
  const updateVocabSimilar = () => {
    let newVocabSimilar = [];

    // Check each expression and add to the newVocabSimilar array if its length is greater than 0
    if (vocabSimilarOne.length > 0) {
      newVocabSimilar.push(vocabSimilarOne);
    }
    if (vocabSimilarTwo.length > 0) {
      newVocabSimilar.push(vocabSimilarTwo);
    }
    if (vocabSimilarThree.length > 0) {
      newVocabSimilar.push(vocabSimilarThree);
    }
    if (vocabSimilarFour.length > 0) {
      newVocabSimilar.push(vocabSimilarFour);
    }

    // Update the vocabSimilar state
    setVocabSimilar(newVocabSimilar);
  };

  const [generatingSim, setGeneratingSim] = useState(false)
  const [showRegenSimOne, setShowRegenSimOne] = useState(false)
  const [showRegenSimTwo, setShowRegenSimTwo] = useState(false)
  const [showRegenSimThree, setShowRegenSimThree] = useState(false)
  const [showRegenSimFour, setShowRegenSimFour] = useState(false)
  const [generatedAiSim, setGeneratedAiSim] = useState(false)

  const genSimTwo = () => {
    if (genSimData) {
      const randomIndex = Math.floor(Math.random() * genSimData.similar.length);
      typeWriter(setVocabSimilarTwo, genSimData.similar[randomIndex])
    }
  }
  const clearSimilarTwo = () => {
    setVocabSimilarTwo("")
    setVocabSimilarShowCount(vocabSimilarShowCount - 1)
  }
  const genSimThree = () => {
    if (genSimData) {
      const randomIndex = Math.floor(Math.random() * genSimData.similar.length);
      typeWriter(setVocabSimilarThree, genSimData.similar[randomIndex])
    }
  }
  const clearSimilarThree = () => {
    setVocabSimilarThree("")
    setVocabSimilarShowCount(vocabSimilarShowCount - 1)
  }
  const genSimFour = () => {
    if (genSimData) {
      const randomIndex = Math.floor(Math.random() * genSimData.similar.length);
      typeWriter(setVocabSimilarFour, genSimData.similar[randomIndex])
    }
  }
  const clearSimilarFour = () => {
    setVocabSimilarFour("")
    setVocabSimilarShowCount(vocabSimilarShowCount - 1)
  }
  /* Generate Ai Similars Data */
  const getGenSim = () => {
    return fetch(`${apiUrl}/openai/similar`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json", // Ensure Content-Type is set to JSON
      },
      body: JSON.stringify({ simVocab: vocabText, simLabel: checkedLabels }), // Send the correct data in the body
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Network response was not ok');
        }
        return res.json(); // Parse the response as JSON
      });
  };

  const { data: genSimData } = useQuery(["aiSimData", vocabText], getGenSim,
    {
      enabled: !!vocabText && generatingSim, // Ensure that vocabText is not empty
      keepPreviousData: true,
      staleTime: 5000,
    }
  );

  const generateAiSimilars = useMutation({
    mutationFn: async () => {
      return await fetch(`${apiUrl}/openai/similar`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          simVocab: vocabText,
          simLabel: checkedLabels,
          vocabDef: vocabDefinition,
        }),
      }).then((res) => res.json()); // Ensure the response is parsed as JSON
    },
    onError: (error, _styleName, context) => {
      console.log("Error fetching:" + context?.id + error);
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({ queryKey: ["aiSimData"] });

      setGeneratedAiSim(true);

      if (data?.similar) {
        typeWriter(setVocabSimilarOne, data.similar[0]);
        if (vocabSimilarShowCount === 4) {
          typeWriter(setVocabSimilarFour, data.similar[3]);
          typeWriter(setVocabSimilarThree, data.similar[2]);
          typeWriter(setVocabSimilarTwo, data.similar[1]);
          typeWriter(setVocabSimilarOne, data.similar[0]);
        }
        if (vocabSimilarShowCount === 3) {
          typeWriter(setVocabSimilarThree, data.similar[2]);
          typeWriter(setVocabSimilarTwo, data.similar[1]);
          typeWriter(setVocabSimilarOne, data.similar[0]);
        }
        if (vocabSimilarShowCount === 2) {
          typeWriter(setVocabSimilarTwo, data.similar[1]);
          typeWriter(setVocabSimilarOne, data.similar[0]);
        }
      }

      setGeneratingSim(false)
    },
  });

  const handleGenAiSim = () => {
    setGeneratingSim(true)
    if (vocabText.length < 1) {
      setRequireVocabText(true)
      return
    }
    generateAiSimilars.mutate()
  }

  const [vocabExpressionOne, setVocabExpressionOne] = useState("")
  const [vocabExpressionTwo, setVocabExpressionTwo] = useState("")
  const [vocabExpressionThree, setVocabExpressionThree] = useState("")
  const [vocabExpressionFour, setVocabExpressionFour] = useState("")
  const [vocabExpressionFive, setVocabExpressionFive] = useState("")
  const [vocabExpressionSix, setVocabExpressionSix] = useState("")
  const [vocabExpressionSeven, setVocabExpressionSeven] = useState("")
  const [vocabExpressionEight, setVocabExpressionEight] = useState("")
  const [vocabExpression, setVocabExpression] = useState([]) // max 8
  const [vocabExpressionShowCount, setVocabExpressionShowCount] = useState(1)
  const incrementVocabExpressionCount = () => {
    if (vocabExpressionShowCount >= 8) return
    setVocabExpressionShowCount(vocabExpressionShowCount + 1)
  }
  const updateVocabExpression = () => {
    let newVocabExpression = [];

    // Check each expression and add to the newVocabExpression array if its length is greater than 0
    if (vocabExpressionOne.length > 0) {
      newVocabExpression.push(vocabExpressionOne);
    }
    if (vocabExpressionTwo.length > 0) {
      newVocabExpression.push(vocabExpressionTwo);
    }
    if (vocabExpressionThree.length > 0) {
      newVocabExpression.push(vocabExpressionThree);
    }
    if (vocabExpressionFour.length > 0) {
      newVocabExpression.push(vocabExpressionFour);
    }
    if (vocabExpressionFive.length > 0) {
      newVocabExpression.push(vocabExpressionFive);
    }
    if (vocabExpressionSix.length > 0) {
      newVocabExpression.push(vocabExpressionSix);
    }
    if (vocabExpressionSeven.length > 0) {
      newVocabExpression.push(vocabExpressionSeven);
    }
    if (vocabExpressionEight.length > 0) {
      newVocabExpression.push(vocabExpressionEight);
    }

    // Update the vocabExpression state
    setVocabExpression(newVocabExpression);
  };

  const [generatingExp, setGeneratingExp] = useState(false)
  const [showRegenExpOne, setShowRegenExpOne] = useState(false)
  const [showRegenExpTwo, setShowRegenExpTwo] = useState(false)
  const [showRegenExpThree, setShowRegenExpThree] = useState(false)
  const [showRegenExpFour, setShowRegenExpFour] = useState(false)
  const [showRegenExpFive, setShowRegenExpFive] = useState(false)
  const [showRegenExpSix, setShowRegenExpSix] = useState(false)
  const [showRegenExpSeven, setShowRegenExpSeven] = useState(false)
  const [showRegenExpEight, setShowRegenExpEight] = useState(false)
  const [generatedAiExp, setGeneratedAiExp] = useState(false)

  const genExpTwo = () => {
    if (genExpData) {
      const randomIndex = Math.floor(Math.random() * genExpData.expression.length);
      typeWriter(setVocabExpressionTwo, genExpData.expression[randomIndex])
    }
  }
  const clearExpressionTwo = () => {
    setVocabExpressionTwo("")
    setVocabExpressionShowCount(vocabExpressionShowCount - 1)
  }
  const genExpThree = () => {
    if (genExpData) {
      const randomIndex = Math.floor(Math.random() * genExpData.expression.length);
      typeWriter(setVocabExpressionThree, genExpData.expression[randomIndex])
    }
  }
  const clearExpressionThree = () => {
    setVocabExpressionThree("")
    setVocabExpressionShowCount(vocabExpressionShowCount - 1)
  }
  const genExpFour = () => {
    if (genExpData) {
      const randomIndex = Math.floor(Math.random() * genExpData.expression.length);
      typeWriter(setVocabExpressionFour, genExpData.expression[randomIndex])
    }
  }
  const clearExpressionFour = () => {
    setVocabExpressionFour("")
    setVocabExpressionShowCount(vocabExpressionShowCount - 1)
  }
  const genExpFive = () => {
    if (genExpData) {
      const randomIndex = Math.floor(Math.random() * genExpData.expression.length);
      typeWriter(setVocabExpressionFive, genExpData.expression[randomIndex])
    }
  }
  const clearExpressionFive = () => {
    setVocabExpressionFive("")
    setVocabExpressionShowCount(vocabExpressionShowCount - 1)
  }
  const genExpSix = () => {
    if (genExpData) {
      const randomIndex = Math.floor(Math.random() * genExpData.expression.length);
      typeWriter(setVocabExpressionSix, genExpData.expression[randomIndex])
    }
  }
  const clearExpressionSix = () => {
    setVocabExpressionSix("")
    setVocabExpressionShowCount(vocabExpressionShowCount - 1)
  }
  const genExpSeven = () => {
    if (genExpData) {
      const randomIndex = Math.floor(Math.random() * genExpData.expression.length);
      typeWriter(setVocabExpressionSeven, genExpData.expression[randomIndex])
    }
  }
  const clearExpressionSeven = () => {
    setVocabExpressionSeven("")
    setVocabExpressionShowCount(vocabExpressionShowCount - 1)
  }
  const genExpEight = () => {
    if (genExpData) {
      const randomIndex = Math.floor(Math.random() * genExpData.expression.length);
      typeWriter(setVocabExpressionEight, genExpData.expression[randomIndex])
    }
  }
  const clearExpressionEight = () => {
    setVocabExpressionEight("")
    setVocabExpressionShowCount(vocabExpressionShowCount - 1)
  }

  /* Generate Ai Expressions Data */
  const getGenExp = () => {
    return fetch(`${apiUrl}/openai/expression`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json", // Ensure Content-Type is set to JSON
      },
      body: JSON.stringify({ expVocab: vocabText, expLabel: checkedLabels }), // Send the correct data in the body
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Network response was not ok');
        }
        return res.json(); // Parse the response as JSON
      });
  };

  const { data: genExpData } = useQuery(["aiExpData", vocabText], getGenExp,
    {
      enabled: !!vocabText && generatingExp, // Ensure that vocabText is not empty
      keepPreviousData: true,
      staleTime: 5000,
    }
  );

  const generateAiExpressions = useMutation({
    mutationFn: async () => {
      return await fetch(`${apiUrl}/openai/expression`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          expVocab: vocabText,
          expLabel: checkedLabels,
          vocabDef: vocabDefinition,
        }),
      }).then((res) => res.json()); // Ensure the response is parsed as JSON
    },
    onError: (error, _styleName, context) => {
      console.log("Error fetching:" + context?.id + error);
    },
    onSettled: (data) => {
      queryClient.invalidateQueries({ queryKey: ["aiExpData"] });

      setGeneratedAiExp(true);

      if (data?.expression) {
        typeWriter(setVocabExpressionOne, data.expression[0]);
        if (vocabExpressionShowCount === 8) {
          typeWriter(setVocabExpressionEight, data.expression[7]);
          typeWriter(setVocabExpressionSeven, data.expression[6]);
          typeWriter(setVocabExpressionSix, data.expression[5]);
          typeWriter(setVocabExpressionFive, data.expression[4]);
          typeWriter(setVocabExpressionFour, data.expression[3]);
          typeWriter(setVocabExpressionThree, data.expression[2]);
          typeWriter(setVocabExpressionTwo, data.expression[1]);
          typeWriter(setVocabExpressionOne, data.expression[0]);
        }
        if (vocabExpressionShowCount === 7) {
          typeWriter(setVocabExpressionSeven, data.expression[6]);
          typeWriter(setVocabExpressionSix, data.expression[5]);
          typeWriter(setVocabExpressionFive, data.expression[4]);
          typeWriter(setVocabExpressionFour, data.expression[3]);
          typeWriter(setVocabExpressionThree, data.expression[2]);
          typeWriter(setVocabExpressionTwo, data.expression[1]);
          typeWriter(setVocabExpressionOne, data.expression[0]);
        }
        if (vocabExpressionShowCount === 6) {
          typeWriter(setVocabExpressionSix, data.expression[5]);
          typeWriter(setVocabExpressionFive, data.expression[4]);
          typeWriter(setVocabExpressionFour, data.expression[3]);
          typeWriter(setVocabExpressionThree, data.expression[2]);
          typeWriter(setVocabExpressionTwo, data.expression[1]);
          typeWriter(setVocabExpressionOne, data.expression[0]);
        }
        if (vocabExpressionShowCount === 5) {
          typeWriter(setVocabExpressionFive, data.expression[4]);
          typeWriter(setVocabExpressionFour, data.expression[3]);
          typeWriter(setVocabExpressionThree, data.expression[2]);
          typeWriter(setVocabExpressionTwo, data.expression[1]);
          typeWriter(setVocabExpressionOne, data.expression[0]);
        }
        if (vocabExpressionShowCount === 4) {
          typeWriter(setVocabExpressionFour, data.expression[3]);
          typeWriter(setVocabExpressionThree, data.expression[2]);
          typeWriter(setVocabExpressionTwo, data.expression[1]);
          typeWriter(setVocabExpressionOne, data.expression[0]);
        }
        if (vocabExpressionShowCount === 3) {
          typeWriter(setVocabExpressionThree, data.expression[2]);
          typeWriter(setVocabExpressionTwo, data.expression[1]);
          typeWriter(setVocabExpressionOne, data.expression[0]);
        }
        if (vocabExpressionShowCount === 2) {
          typeWriter(setVocabExpressionTwo, data.expression[1]);
          typeWriter(setVocabExpressionOne, data.expression[0]);
        }
      }

      setGeneratingExp(false)
    },
  });

  const handleGenAiExp = () => {
    setGeneratingExp(true)
    if (vocabText.length < 1) {
      setRequireVocabText(true)
      return
    }
    generateAiExpressions.mutate()
  }

  const [vocabSentenceOne, setVocabSentenceOne] = useState("")
  const [vocabSentenceTwo, setVocabSentenceTwo] = useState("")
  const [vocabSentenceThree, setVocabSentenceThree] = useState("")
  const [vocabSentenceFour, setVocabSentenceFour] = useState("")
  const [vocabSentence, setVocabSentence] = useState([]) // max 4
  const [vocabSentenceShowCount, setVocabSentenceShowCount] = useState(1)
  const incrementVocabSentenceCount = () => {
    if (vocabSentenceShowCount >= 4) return
    setVocabSentenceShowCount(vocabSentenceShowCount + 1)
  }
  const updateVocabSentence = () => {
    let newVocabSentence = [];

    // Check each expression and add to the newVocabSentence array if its length is greater than 0
    if (vocabSentenceOne.length > 0) {
      newVocabSentence.push(vocabSentenceOne);
    }
    if (vocabSentenceTwo.length > 0) {
      newVocabSentence.push(vocabSentenceTwo);
    }
    if (vocabSentenceThree.length > 0) {
      newVocabSentence.push(vocabSentenceThree);
    }
    if (vocabSentenceFour.length > 0) {
      newVocabSentence.push(vocabSentenceFour);
    }

    // Update the vocabSentence state
    setVocabSentence(newVocabSentence);
  };

  const [openSnackbar, setOpenSnackbar] = useState(false)
  const handleSnackbarOpen = () => {
    setOpenSnackbar(true)
  }
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return
    }
    setOpenSnackbar(false)
  }
  const createAction = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleSnackbarClose}>
        <IoClose />
      </IconButton>
    </>
  )

  const createVocabMutation = useMutation({
    mutationFn: async () => {
      return await fetch(`${apiUrl}/vocabs`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: _id,
          text: vocabText,
          pinyin: vocabPinyin,
          difficulty: vocabLevel,
          label: checkedLabels,
          definition: vocabDefinition,
          similar: vocabSimilar,
          expression: vocabExpression,
          sentence: vocabSentence
        }),
      })
    },
    onError: (error, _styleName, context) => {
      console.log("Error fetching:" + context.id + error)
    },
    onSettled: () => {
      handleSnackbarOpen()
      queryClient.invalidateQueries({ queryKey: ["allVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["pinnedVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["intVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["advVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["natVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["searchVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["labeledVocabsData"] })
      setVocabText("")
      setVocabPinyin("")
      setVocabDefinition("")
      setVocabSimilarOne("")
      setVocabSimilarTwo("")
      setVocabSimilarThree("")
      setVocabSimilarFour("")
      setVocabExpressionOne("")
      setVocabExpressionTwo("")
      setVocabExpressionThree("")
      setVocabExpressionFour("")
      setVocabExpressionFive("")
      setVocabExpressionSix("")
      setVocabExpressionSeven("")
      setVocabExpressionEight("")
      setVocabSentenceOne("")
      setVocabSentenceTwo("")
    }
  })

  const handleCreateVocab = () => {
    if (vocabText.length < 1) {
      setRequireVocabText(true)
      return
    }
    updateVocabSimilar()
    updateVocabExpression()
    updateVocabSentence()
    createVocabMutation.mutate()
    setTimeout(() => {
      dispatch(setRefreshKey({ refreshKey: refreshKey + 1 }))
    }, 400)
  }

  useEffect(() => {
    const handleKeyPress = (event) => {
      // Check if both Shift and Enter are pressed
      if (event.shiftKey && event.key === 'Enter') {
        event.preventDefault(); // Optional: Prevent default Enter behavior (e.g., submitting a form)
        handleCreateVocab(); // Call your function
      }
    };

    // Add the keydown event listener when the component mounts
    document.addEventListener('keydown', handleKeyPress);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [vocabText, createVocabMutation]); // Add necessary dependencies like vocabText and createVocabMutation


  return (
    <>
      {isLandscape ? (
        <Button
          startIcon={<IoLanguageSharp size={16} color={theme.palette.neutral.darker} />}
          onClick={handleDialogOpen}
          className={(mode === "light") ? "gradient-button" : "gradient-button-dark"}
          size="medium"
          sx={
            (mode === "light") ?
              {
                color: theme.palette.neutral.dark,
                margin: "0.5rem 1rem",
                padding: "0.5rem 1rem",
                borderRadius: "6rem",
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "none",
                border: `1px solid ${theme.palette.neutral.light}`,
                ":hover": {
                  backgroundColor: theme.palette.background.alt
                }
              }
              :
              {
                color: theme.palette.primary.main,
                margin: "0.5rem 1rem",
                padding: "0.5rem 1rem",
                borderRadius: "6rem",
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "none",
                border: `1px solid ${theme.palette.neutral.light}`,
                ":hover": {
                  backgroundColor: theme.palette.background.alt
                }
              }
          }
        >
          Add
        </Button>)
        :
        (<Button
          onClick={handleDialogOpen}
          className={(mode === "light") ? "gradient-button" : "gradient-button-dark"}
          size="medium"
          sx={
            (mode === "light") ?
              {
                color: theme.palette.neutral.dark,
                margin: "0.5rem 1.75rem",
                padding: "0.5rem 1.75rem",
                borderRadius: "6rem",
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "none",
                border: `1px solid ${theme.palette.neutral.light}`,
                ":hover": {
                  backgroundColor: theme.palette.background.alt
                }
              }
              :
              {
                color: theme.palette.primary.main,
                margin: "0.5rem 1.75rem",
                padding: "0.5rem 1.75rem",
                borderRadius: "6rem",
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "none",
                border: `1px solid ${theme.palette.neutral.light}`,
                ":hover": {
                  backgroundColor: theme.palette.background.alt
                }
              }
          }
        >
          <IoMdAdd />{text}
        </Button>)
      }
      <Dialog
        open={openDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleDialogClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiDialog-paper": {
            width: isLandscape ? "22%" : "100%",
            borderRadius: "0.4rem",
            display: "flex",
            backgroundColor: mode === "light" ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 11, 13, 0.2)",
            backgroundImage: `linear-gradient(
                to top left, 
                rgba(255, 255, 255, 0.15), 
                rgba(255, 255, 255, 0.05)
              )`,
            backdropFilter: "blur(10px)", // Apply the glass effect
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)"
          },
          "& .MuiDialog-paperElevation24": {
            boxShadow: "none", // Remove elevation shadows
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundImage: `linear-gradient(
                to right, 
                rgba(3, 241, 232, 0.075), 
                rgba(18, 77, 115, 0.05)
              )`
            },
          },
        }}
      >
        <Box
          margin={!isNonMobileScreens ? "0.25rem 0.25rem 1.5rem 0.25rem" : "1.5rem 1.5rem 2rem 1.5rem"}
          padding={isPortrait ? 1 : "0 0.5rem"}
        >
          <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} pb={2}>
            <Typography
              variant={isPortrait ? "h5" : "h4"}
              fontWeight={700}
              p={isPortrait ? 1 : undefined}
            >
              Create Vocabulary
            </Typography>
            <IoMdClose size={24} style={{ cursor: "pointer", color: theme.palette.neutral.main }} onClick={handleDialogClose} />
          </Stack>


          <Stack spacing={1.5}>

            {/* ----- Chinese Text Input Field ----- */}
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
              <Typography>Text</Typography>
              <InputBase
                id={uuidv4()}
                placeholder="中文"
                onChange={(e) => setVocabText(e.target.value)}
                value={vocabText}
                onKeyDown={() => setRequireVocabText(false)}
                required={true}
                sx={{
                  fontSize: isWideScreens ? "1.5rem" : isQHDScreens ? "1.25rem" : "1rem",
                  width: "100%",
                  color: theme.palette.neutral.dark,
                  borderRadius: "0.5rem",
                  padding: "0.25rem 0.5rem",
                  margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem",
                  border: requireVocabText ? `solid 2px ${theme.palette.secondary.main}` : `solid 1px ${theme.palette.neutral.light}`,
                }}
              />
            </Stack>

            {/* ----- Pinyin ----- */}
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
              <Typography>Pinyin</Typography>
              <InputBase
                id={uuidv4()}
                placeholder="拼音"
                onChange={(e) => setVocabPinyin(e.target.value)}
                value={vocabPinyin}
                required={true}
                sx={{
                  width: "100%",
                  fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1.15rem" : "1rem",
                  color: theme.palette.neutral.dark,
                  border: `solid 1px ${theme.palette.neutral.light}`,
                  borderRadius: "0.5rem",
                  padding: "0.25rem 0.5rem",
                  margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                }}
              />
            </Stack>

            {/* ----- Difficulty Field ----- */}
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
              <Typography>Difficulty</Typography>
              <Stack direction={"row"}>
                <Tooltip title="Easy" placement="top">
                  <IconButton
                    onMouseOver={() => setVocabLevelHover("1")}
                    onMouseLeave={() => setVocabLevelHover("1")}
                    onClick={() => setVocabLevel("1")}
                  >
                    {(vocabLevelHover === "1" ||
                      vocabLevel === "1" ||
                      vocabLevel === "2" ||
                      vocabLevel === "3" ||
                      vocabLevel === "4") ?
                      <FaCircle
                        size={24}
                        style={
                          {
                            color: vocabLevel === "1" ?
                              theme.palette.primary.main :
                              vocabLevel === "2" ?
                                theme.palette.tertiary.main :
                                vocabLevel === "3" ?
                                  theme.palette.secondary.main :
                                  "#d934eb"
                          }}
                      /> :
                      <FaRegCircle
                        size={24}
                        style={
                          {
                            color: vocabLevel === "1" ?
                              theme.palette.primary.main :
                              vocabLevel === "2" ?
                                theme.palette.tertiary.main :
                                vocabLevel === "3" ?
                                  theme.palette.secondary.main :
                                  "#d934eb"
                          }}
                      />
                    }
                  </IconButton>
                </Tooltip>
                <Tooltip title="Intermediate" placement="top">
                  <IconButton
                    onMouseOver={() => setVocabLevelHover("2")}
                    onMouseLeave={() => setVocabLevelHover("1")}
                    onClick={() => setVocabLevel("2")}
                  >
                    {(vocabLevelHover === "2" ||
                      vocabLevel === "2" ||
                      vocabLevel === "3" ||
                      vocabLevel === "4" ||
                      vocabLevelHover === "3" ||
                      vocabLevelHover === "4") ?
                      <FaCircle
                        size={24}
                        style={
                          {
                            color: vocabLevel === "1" ?
                              theme.palette.primary.main :
                              vocabLevel === "2" ?
                                theme.palette.tertiary.main :
                                vocabLevel === "3" ?
                                  theme.palette.secondary.main :
                                  "#d934eb"
                          }}
                      /> :
                      <FaRegCircle
                        size={24}
                        style={
                          {
                            color: vocabLevel === "1" ?
                              theme.palette.primary.main :
                              vocabLevel === "2" ?
                                theme.palette.tertiary.main :
                                vocabLevel === "3" ?
                                  theme.palette.secondary.main :
                                  "#d934eb"
                          }}
                      />
                    }
                  </IconButton>
                </Tooltip>
                <Tooltip title="Advanced" placement="top">
                  <IconButton
                    onMouseOver={() => setVocabLevelHover("3")}
                    onMouseLeave={() => setVocabLevelHover("1")}
                    onClick={() => setVocabLevel("3")}
                  >
                    {(vocabLevelHover === "3" ||
                      vocabLevel === "3" ||
                      vocabLevel === "4" ||
                      vocabLevelHover === "4") ?
                      <FaCircle
                        size={24}
                        style={
                          {
                            color: vocabLevel === "1" ?
                              theme.palette.primary.main :
                              vocabLevel === "2" ?
                                theme.palette.tertiary.main :
                                vocabLevel === "3" ?
                                  theme.palette.secondary.main :
                                  "#d934eb"
                          }}
                      /> :
                      <FaRegCircle
                        size={24}
                        style={
                          {
                            color: vocabLevel === "1" ?
                              theme.palette.primary.main :
                              vocabLevel === "2" ?
                                theme.palette.tertiary.main :
                                vocabLevel === "3" ?
                                  theme.palette.secondary.main :
                                  "#d934eb"
                          }}
                      />
                    }
                  </IconButton>
                </Tooltip>
                <Tooltip title="Require Native Clarification" placement="top">
                  <IconButton
                    onMouseOver={() => setVocabLevelHover("4")}
                    onMouseLeave={() => setVocabLevelHover("1")}
                    onClick={() => setVocabLevel("4")}
                  >
                    {(vocabLevelHover === "4" || vocabLevel === "4") ?
                      <FaCircle
                        size={24}
                        style={
                          {
                            color: vocabLevel === "1" ?
                              theme.palette.primary.main :
                              vocabLevel === "2" ?
                                theme.palette.tertiary.main :
                                vocabLevel === "3" ?
                                  theme.palette.secondary.main :
                                  "#d934eb"
                          }}
                      /> :
                      <FaRegCircle
                        size={24}
                        style={
                          {
                            color: vocabLevel === "1" ?
                              theme.palette.primary.main :
                              vocabLevel === "2" ?
                                theme.palette.tertiary.main :
                                vocabLevel === "3" ?
                                  theme.palette.secondary.main :
                                  "#d934eb"
                          }}
                      />
                    }
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            {/* ----- Label Field ----- */}
            <Stack direction={"row"} alignItems={showLabels ? "flex-start" : "center"} spacing={showLabels ? 1 : 2}>
              {showLabels ?
                <IconButton onClick={() => setShowLabels(false)}>
                  <AiOutlineEyeInvisible size={16} color={theme.palette.neutral.darker} />
                </IconButton>
                :
                <Typography>Label</Typography>
              }
              <Stack direction={"row"} spacing={1} flexWrap={"wrap"} sx={{ flex: 1 }}>
                {!showLabels &&
                  <Button
                    startIcon={<AiOutlineEye size={16} color={theme.palette.neutral.darker} />}
                    onClick={() => setShowLabels(true)}
                    className={(mode === "light") ? "gradient-button" : "gradient-button-dark"}
                    size="medium"
                    sx={
                      (mode === "light") ?
                        {
                          color: theme.palette.neutral.dark,
                          margin: "0.5rem 0",
                          padding: "0.5rem 1rem",
                          borderRadius: "6rem",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          textTransform: "none",
                          border: `1px solid ${theme.palette.neutral.light}`,
                          ":hover": {
                            backgroundColor: theme.palette.background.alt
                          }
                        }
                        :
                        {
                          color: theme.palette.primary.main,
                          margin: "0.5rem 0",
                          padding: "0.5rem 1rem",
                          borderRadius: "6rem",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          textTransform: "none",
                          border: `1px solid ${theme.palette.neutral.light}`,
                          ":hover": {
                            backgroundColor: theme.palette.background.alt
                          }
                        }
                    }
                  >
                    Show
                  </Button>
                }
                {!showLabels &&
                  <>
                    {checkedLabels?.map((text, index) => (
                      <Stack key={index} direction="row" spacing={0} p={"0.25rem 0"} alignItems={"center"}>
                        <Typography
                          onClick={() => handleCheckboxChange(text)}
                          color={theme.palette.neutral.mid}
                          fontSize={checkedLabels?.length === 1 ? "0.9rem" : checkedLabels?.length === 2 ? "0.8rem" : "0.7rem"}
                        >
                          {text}
                        </Typography>
                      </Stack>
                    ))}
                  </>}
                <Collapse in={showLabels} timeout={350} easing={"cubic-bezier(0.4, 0, 0.2, 1)"} sx={{ width: "100%" }}>
                  <Stack direction={"row"} flexWrap={"wrap"} sx={{ p: 0.25 }}>
                    {labelsData?.[0]?.label?.map((text, index) => (
                      <Stack key={index} direction="row" spacing={0} p={"0.125rem 0.25rem"} alignItems={"center"} sx={{ cursor: "pointer" }}>
                        <Checkbox
                          color="primary"
                          value={text}
                          checked={checkedLabels?.includes(text)} // Control the checked state
                          onChange={() => handleCheckboxChange(text)} // Handle checkbox change
                          sx={{
                            p: 0.5,
                          }}
                        />
                        <Typography
                          onClick={() => handleCheckboxChange(text)}
                          color={checkedLabels?.includes(text) ? theme.palette.primary.main : theme.palette.neutral.darker}
                          fontSize={isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.8rem"}
                        >
                          {text}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Collapse>
              </Stack>
            </Stack>

            {/* ----- Definition Field ----- */}
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
              <Tooltip title={generatingDetailedDef ? "Generate Detailed Definition" : "Generate Definition"} placement="left">
                <IconButton onClick={() => setGeneratingDetailedDef(!generatingDetailedDef)}>
                  {generatingDetailedDef ?
                    <PiBookOpenText size={20} color={theme.palette.tertiary.main} /> :
                    <PiBookOpen size={20} color={theme.palette.neutral.darker} />
                  }
                </IconButton>
              </Tooltip>
              <InputBase
                id={uuidv4()}
                placeholder="Definition"
                onChange={(e) => setVocabDefinition(e.target.value)}
                value={vocabDefinition}
                multiline={true}
                minRows={2}
                required={true}
                sx={{
                  fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                  width: "80%",
                  color: theme.palette.neutral.dark,
                  border: `solid 1px ${theme.palette.neutral.light}`,
                  borderRadius: "0.5rem",
                  padding: "0.25rem 0.5rem",
                  margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                }}
              />
              {generatingDef ?
                (
                  <HashLoader
                    color={theme.palette.primary.main}
                    loading={true}
                    size={16}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                  />
                )
                :
                (
                  <Stack
                    onMouseOver={() => setShowRegenDef(true)}
                    onMouseLeave={() => setShowRegenDef(false)}
                  >
                    {(showRegenDef && vocabDefinition.length > 1) ?
                      <Tooltip title="Regenerate" placement="right">
                        <IconButton onClick={checkedLabels.includes("grammar") ? handleRequestGenGrammar : handleRequestGenDef}>
                          <IoRefresh size={16} />
                        </IconButton>
                      </Tooltip>
                      :
                      <Tooltip title="Generate Definition" placement="right">
                        <IconButton onClick={checkedLabels.includes("grammar") ? handleRequestGenGrammar : handleRequestGenDef}>
                          {vocabDefinition?.length < 1 ?
                            <PiSparkle size={16} />
                            :
                            <PiSparkleFill size={16} />
                          }
                        </IconButton>
                      </Tooltip>
                    }
                  </Stack>
                )
              }
            </Stack>

            {/* ----- Colloquial Field ----- */}
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
              <Tooltip title="Expand / Collapse" placement="left">
                <IconButton onClick={() => setMultilineColloquial(!multilineColloquial)}>
                  <PiChatCircle size={20} color={theme.palette.neutral.darker} />
                </IconButton>
              </Tooltip>
              <InputBase
                id={uuidv4()}
                placeholder="Colloquial"
                onChange={(e) => setVocabColloquial(e.target.value)}
                value={vocabColloquial}
                multiline={multilineColloquial}
                minRows={multilineColloquial ? 2 : undefined}
                required={true}
                sx={{
                  fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                  width: "80%",
                  color: theme.palette.neutral.dark,
                  border: `solid 1px ${theme.palette.neutral.light}`,
                  borderRadius: "0.5rem",
                  padding: "0.25rem 0.5rem",
                  margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                }}
              />
              {generatingColloquial ?
                (
                  <HashLoader
                    color={theme.palette.primary.main}
                    loading={true}
                    size={16}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                  />
                )
                :
                (
                  <Stack
                    onMouseOver={() => setShowRegenColloquial(true)}
                    onMouseLeave={() => setShowRegenColloquial(false)}
                  >
                    {(showRegenColloquial && vocabColloquial.length > 1) ?
                      <Tooltip title="Regenerate" placement="right">
                        <IconButton onClick={handleRequestGenColloquial}>
                          <IoRefresh size={16} />
                        </IconButton>
                      </Tooltip>
                      :
                      <Tooltip title="Generate Colloquial" placement="right">
                        <IconButton onClick={handleRequestGenColloquial}>
                          {vocabColloquial?.length < 1 ?
                            <PiSparkle size={16} />
                            :
                            <PiSparkleFill size={16} />
                          }
                        </IconButton>
                      </Tooltip>
                    }
                  </Stack>
                )
              }
            </Stack>

            {/* ----- Similar Text Input Field ----- */}
            <Stack direction={"row"} alignItems={"center"} spacing={1}>
              <Stack pr={1}>
                <TbChartCircles size={20} color={theme.palette.neutral.darker} />
              </Stack>
              <InputBase
                id={uuidv4()}
                placeholder="Similar"
                onChange={(e) => setVocabSimilarOne(e.target.value)}
                value={vocabSimilarOne}
                required={true}
                sx={{
                  fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                  width: isLandscape ? "80%" : "70%",
                  color: theme.palette.neutral.dark,
                  border: `solid 1px ${theme.palette.neutral.light}`,
                  borderRadius: "0.5rem",
                  padding: "0.25rem 0.5rem",
                  margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                }}
              />
              <IconButton onClick={incrementVocabSimilarCount}>
                <IoMdAdd size={16} />
              </IconButton>
              {(vocabSimilarOne?.length < 1 && generatingSim) ?
                (
                  <HashLoader
                    color={theme.palette.primary.main}
                    loading={true}
                    size={16}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                  />
                )
                :
                (
                  <Stack
                    onMouseOver={() => setShowRegenSimOne(true)}
                    onMouseLeave={() => setShowRegenSimOne(false)}
                  >
                    {(showRegenSimOne && vocabSimilarOne?.length > 1) ?
                      <Tooltip title="Regenerate All" placement="right">
                        <IconButton onClick={handleGenAiSim}>
                          <IoRefresh size={16} />
                        </IconButton>
                      </Tooltip>
                      :
                      <Tooltip title="Generate with Ai" placement="right">
                        <IconButton onClick={handleGenAiSim}>
                          {vocabSimilarOne?.length < 1 ?
                            <PiSparkle size={16} />
                            :
                            <PiSparkleFill size={16} />
                          }
                        </IconButton>
                      </Tooltip>
                    }
                  </Stack>
                )
              }
            </Stack>

            {/* ----- 2nd Similar Text Input Field ----- */}
            <CollapseField open={vocabSimilarShowCount > 1}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <TbChartCircles opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Similar"
                  onChange={(e) => setVocabSimilarTwo(e.target.value)}
                  value={vocabSimilarTwo}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "80%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={clearSimilarTwo}>
                  <IoMdClose size={16} />
                </IconButton>
                {generatedAiSim && (
                  <>
                    {(vocabSimilarTwo?.length < 1 && generatingSim) ?
                      (
                        <HashLoader
                          color={theme.palette.primary.main}
                          loading={true}
                          size={16}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                        />
                      )
                      :
                      (
                        <Stack
                          onMouseOver={() => setShowRegenSimTwo(true)}
                          onMouseLeave={() => setShowRegenSimTwo(false)}
                        >
                          {(showRegenSimTwo && vocabSimilarTwo?.length > 1) ?
                            <Tooltip title="Regenerate" placement="right">
                              <IconButton onClick={genSimTwo}>
                                <IoRefresh size={16} />
                              </IconButton>
                            </Tooltip>
                            :
                            <Tooltip title="Generate" placement="right">
                              <IconButton onClick={genSimTwo}>
                                {vocabSimilarTwo?.length < 1 ?
                                  <PiSparkle size={16} />
                                  :
                                  <PiSparkleFill size={16} />
                                }
                              </IconButton>
                            </Tooltip>
                          }
                        </Stack>
                      )
                    }
                  </>
                )}
              </Stack>
            </CollapseField>

            {/* ----- 3rd Similar Text Input Field ----- */}
            <CollapseField open={vocabSimilarShowCount > 2}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <TbChartCircles opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Similar"
                  onChange={(e) => setVocabSimilarThree(e.target.value)}
                  value={vocabSimilarThree}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "80%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={clearSimilarThree}>
                  <IoMdClose size={16} />
                </IconButton>
                {generatedAiSim && (
                  <>
                    {(vocabSimilarThree?.length < 1 && generatingSim) ?
                      (
                        <HashLoader
                          color={theme.palette.primary.main}
                          loading={true}
                          size={16}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                        />
                      )
                      :
                      (
                        <Stack
                          onMouseOver={() => setShowRegenSimThree(true)}
                          onMouseLeave={() => setShowRegenSimThree(false)}
                        >
                          {(showRegenSimThree && vocabSimilarThree?.length > 1) ?
                            <Tooltip title="Regenerate" placement="right">
                              <IconButton onClick={genSimThree}>
                                <IoRefresh size={16} />
                              </IconButton>
                            </Tooltip>
                            :
                            <Tooltip title="Generate" placement="right">
                              <IconButton onClick={genSimThree}>
                                {vocabSimilarThree?.length < 1 ?
                                  <PiSparkle size={16} />
                                  :
                                  <PiSparkleFill size={16} />
                                }
                              </IconButton>
                            </Tooltip>
                          }
                        </Stack>
                      )
                    }
                  </>
                )}
              </Stack>
            </CollapseField>

            {/* ----- 4th Similar Text Input Field ----- */}
            <CollapseField open={vocabSimilarShowCount > 3}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <TbChartCircles opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Similar"
                  onChange={(e) => setVocabSimilarFour(e.target.value)}
                  value={vocabSimilarFour}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "80%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={clearSimilarFour}>
                  <IoMdClose size={16} />
                </IconButton>
                {generatedAiSim && (
                  <>
                    {(vocabSimilarFour?.length < 1 && generatingSim) ?
                      (
                        <HashLoader
                          color={theme.palette.primary.main}
                          loading={true}
                          size={16}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                        />
                      )
                      :
                      (
                        <Stack
                          onMouseOver={() => setShowRegenSimFour(true)}
                          onMouseLeave={() => setShowRegenSimFour(false)}
                        >
                          {(showRegenSimFour && vocabSimilarFour?.length > 1) ?
                            <Tooltip title="Regenerate" placement="right">
                              <IconButton onClick={genSimFour}>
                                <IoRefresh size={16} />
                              </IconButton>
                            </Tooltip>
                            :
                            <Tooltip title="Generate" placement="right">
                              <IconButton onClick={genSimFour}>
                                {vocabSimilarFour?.length < 1 ?
                                  <PiSparkle size={16} />
                                  :
                                  <PiSparkleFill size={16} />
                                }
                              </IconButton>
                            </Tooltip>
                          }
                        </Stack>
                      )
                    }
                  </>
                )}
              </Stack>
            </CollapseField>

            {/* ----- Expression Text Input Field ----- */}
            <Stack direction={"row"} alignItems={"center"} spacing={1}>
              <Stack pr={1}>
                <PiChatCircleDots size={20} color={theme.palette.neutral.darker} />
              </Stack>
              <InputBase
                id={uuidv4()}
                placeholder="Expression"
                multiline
                rows={2}
                onChange={(e) => setVocabExpressionOne(e.target.value)}
                value={vocabExpressionOne}
                required={true}
                sx={{
                  fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                  width: isLandscape ? "80%" : "70%",
                  color: theme.palette.neutral.dark,
                  border: `solid 1px ${theme.palette.neutral.light}`,
                  borderRadius: "0.5rem",
                  padding: "0.25rem 0.5rem",
                  margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                }}
              />
              <IconButton onClick={incrementVocabExpressionCount}>
                <IoMdAdd size={16} />
              </IconButton>
              {(vocabExpressionOne?.length < 1 && generatingExp) ?
                (
                  <HashLoader
                    color={theme.palette.primary.main}
                    loading={true}
                    size={16}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                  />
                )
                :
                (
                  <Stack
                    onMouseOver={() => setShowRegenExpOne(true)}
                    onMouseLeave={() => setShowRegenExpOne(false)}
                  >
                    {(showRegenExpOne && vocabExpressionOne?.length > 1) ?
                      <Tooltip title="Regenerate All" placement="right">
                        <IconButton onClick={handleGenAiExp}>
                          <IoRefresh size={16} />
                        </IconButton>
                      </Tooltip>
                      :
                      <Tooltip title="Generate with Ai" placement="right">
                        <IconButton onClick={handleGenAiExp}>
                          {vocabExpressionOne?.length < 1 ?
                            <PiSparkle size={16} />
                            :
                            <PiSparkleFill size={16} />
                          }
                        </IconButton>
                      </Tooltip>
                    }
                  </Stack>
                )
              }
            </Stack>


            {/* ----- 2nd Expression Text Input Field ----- */}
            <CollapseField open={vocabExpressionShowCount > 1}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <PiChatCircleDots opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Expression"
                  multiline
                  rows={2}
                  onChange={(e) => setVocabExpressionTwo(e.target.value)}
                  value={vocabExpressionTwo}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "80%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={clearExpressionTwo}>
                  <IoMdClose size={16} />
                </IconButton>
                {generatedAiExp && (
                  <>
                    {(vocabExpressionTwo?.length < 1 && generatingExp) ?
                      (
                        <HashLoader
                          color={theme.palette.primary.main}
                          loading={true}
                          size={16}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                        />
                      )
                      :
                      (
                        <Stack
                          onMouseOver={() => setShowRegenExpTwo(true)}
                          onMouseLeave={() => setShowRegenExpTwo(false)}
                        >
                          {(showRegenExpTwo && vocabExpressionTwo?.length > 1) ?
                            <Tooltip title="Regenerate" placement="right">
                              <IconButton onClick={genExpTwo}>
                                <IoRefresh size={16} />
                              </IconButton>
                            </Tooltip>
                            :
                            <Tooltip title="Generate" placement="right">
                              <IconButton onClick={genExpTwo}>
                                {vocabExpressionTwo?.length < 1 ?
                                  <PiSparkle size={16} />
                                  :
                                  <PiSparkleFill size={16} />
                                }
                              </IconButton>
                            </Tooltip>
                          }
                        </Stack>
                      )
                    }
                  </>
                )}
              </Stack>
            </CollapseField>

            {/* ----- 3rd Expression Text Input Field ----- */}
            <CollapseField open={vocabExpressionShowCount > 2}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <PiChatCircleDots opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Expression"
                  multiline
                  rows={2}
                  onChange={(e) => setVocabExpressionThree(e.target.value)}
                  value={vocabExpressionThree}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "80%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={clearExpressionThree}>
                  <IoMdClose size={16} />
                </IconButton>
                {generatedAiExp && (
                  <>
                    {(vocabExpressionThree?.length < 1 && generatingExp) ?
                      (
                        <HashLoader
                          color={theme.palette.primary.main}
                          loading={true}
                          size={16}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                        />
                      )
                      :
                      (
                        <Stack
                          onMouseOver={() => setShowRegenExpThree(true)}
                          onMouseLeave={() => setShowRegenExpThree(false)}
                        >
                          {(showRegenExpThree && vocabExpressionThree?.length > 1) ?
                            <Tooltip title="Regenerate" placement="right">
                              <IconButton onClick={genExpThree}>
                                <IoRefresh size={16} />
                              </IconButton>
                            </Tooltip>
                            :
                            <Tooltip title="Generate" placement="right">
                              <IconButton onClick={genExpThree}>
                                {vocabExpressionThree?.length < 1 ?
                                  <PiSparkle size={16} />
                                  :
                                  <PiSparkleFill size={16} />
                                }
                              </IconButton>
                            </Tooltip>
                          }
                        </Stack>
                      )
                    }
                  </>
                )}
              </Stack>
            </CollapseField>

            {/* ----- 4th Expression Text Input Field ----- */}
            <CollapseField open={vocabExpressionShowCount > 3}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <PiChatCircleDots opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Expression"
                  multiline
                  rows={2}
                  onChange={(e) => setVocabExpressionFour(e.target.value)}
                  value={vocabExpressionFour}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "80%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={clearExpressionFour}>
                  <IoMdClose size={16} />
                </IconButton>
                {generatedAiExp && (
                  <>
                    {(vocabExpressionFour?.length < 1 && generatingExp) ?
                      (
                        <HashLoader
                          color={theme.palette.primary.main}
                          loading={true}
                          size={16}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                        />
                      )
                      :
                      (
                        <Stack
                          onMouseOver={() => setShowRegenExpFour(true)}
                          onMouseLeave={() => setShowRegenExpFour(false)}
                        >
                          {(showRegenExpFour && vocabExpressionFour?.length > 1) ?
                            <Tooltip title="Regenerate" placement="right">
                              <IconButton onClick={genExpFour}>
                                <IoRefresh size={16} />
                              </IconButton>
                            </Tooltip>
                            :
                            <Tooltip title="Generate" placement="right">
                              <IconButton onClick={genExpFour}>
                                {vocabExpressionFour?.length < 1 ?
                                  <PiSparkle size={16} />
                                  :
                                  <PiSparkleFill size={16} />
                                }
                              </IconButton>
                            </Tooltip>
                          }
                        </Stack>
                      )
                    }
                  </>
                )}
              </Stack>
            </CollapseField>

            {/* ----- 5th Expression Text Input Field ----- */}
            <CollapseField open={vocabExpressionShowCount > 4}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <PiChatCircleDots opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Expression"
                  multiline
                  rows={2}
                  onChange={(e) => setVocabExpressionFive(e.target.value)}
                  value={vocabExpressionFive}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "80%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={clearExpressionFive}>
                  <IoMdClose size={16} />
                </IconButton>
                {generatedAiExp && (
                  <>
                    {(vocabExpressionFive?.length < 1 && generatingExp) ?
                      (
                        <HashLoader
                          color={theme.palette.primary.main}
                          loading={true}
                          size={16}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                        />
                      )
                      :
                      (
                        <Stack
                          onMouseOver={() => setShowRegenExpFive(true)}
                          onMouseLeave={() => setShowRegenExpFive(false)}
                        >
                          {(showRegenExpFive && vocabExpressionFive?.length > 1) ?
                            <Tooltip title="Regenerate" placement="right">
                              <IconButton onClick={genExpFive}>
                                <IoRefresh size={16} />
                              </IconButton>
                            </Tooltip>
                            :
                            <Tooltip title="Generate" placement="right">
                              <IconButton onClick={genExpFive}>
                                {vocabExpressionFive?.length < 1 ?
                                  <PiSparkle size={16} />
                                  :
                                  <PiSparkleFill size={16} />
                                }
                              </IconButton>
                            </Tooltip>
                          }
                        </Stack>
                      )
                    }
                  </>
                )}
              </Stack>
            </CollapseField>

            {/* ----- 6th Expression Text Input Field ----- */}
            <CollapseField open={vocabExpressionShowCount > 5}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <PiChatCircleDots opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Expression"
                  multiline
                  rows={2}
                  onChange={(e) => setVocabExpressionSix(e.target.value)}
                  value={vocabExpressionSix}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "80%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={clearExpressionSix}>
                  <IoMdClose size={16} />
                </IconButton>
                {generatedAiExp && (
                  <>
                    {(vocabExpressionSix?.length < 1 && generatingExp) ?
                      (
                        <HashLoader
                          color={theme.palette.primary.main}
                          loading={true}
                          size={16}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                        />
                      )
                      :
                      (
                        <Stack
                          onMouseOver={() => setShowRegenExpSix(true)}
                          onMouseLeave={() => setShowRegenExpSix(false)}
                        >
                          {(showRegenExpSix && vocabExpressionSix?.length > 1) ?
                            <Tooltip title="Regenerate" placement="right">
                              <IconButton onClick={genExpSix}>
                                <IoRefresh size={16} />
                              </IconButton>
                            </Tooltip>
                            :
                            <Tooltip title="Generate" placement="right">
                              <IconButton onClick={genExpSix}>
                                {vocabExpressionSix?.length < 1 ?
                                  <PiSparkle size={16} />
                                  :
                                  <PiSparkleFill size={16} />
                                }
                              </IconButton>
                            </Tooltip>
                          }
                        </Stack>
                      )
                    }
                  </>
                )}
              </Stack>
            </CollapseField>

            {/* ----- 7th Expression Text Input Field ----- */}
            <CollapseField open={vocabExpressionShowCount > 6}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <PiChatCircleDots opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Expression"
                  multiline
                  rows={2}
                  onChange={(e) => setVocabExpressionSeven(e.target.value)}
                  value={vocabExpressionSeven}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "80%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={clearExpressionSeven}>
                  <IoMdClose size={16} />
                </IconButton>
                {generatedAiExp && (
                  <>
                    {(vocabExpressionSeven?.length < 1 && generatingExp) ?
                      (
                        <HashLoader
                          color={theme.palette.primary.main}
                          loading={true}
                          size={16}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                        />
                      )
                      :
                      (
                        <Stack
                          onMouseOver={() => setShowRegenExpSeven(true)}
                          onMouseLeave={() => setShowRegenExpSeven(false)}
                        >
                          {(showRegenExpSeven && vocabExpressionSeven?.length > 1) ?
                            <Tooltip title="Regenerate" placement="right">
                              <IconButton onClick={genExpSeven}>
                                <IoRefresh size={16} />
                              </IconButton>
                            </Tooltip>
                            :
                            <Tooltip title="Generate" placement="right">
                              <IconButton onClick={genExpSeven}>
                                {vocabExpressionSeven?.length < 1 ?
                                  <PiSparkle size={16} />
                                  :
                                  <PiSparkleFill size={16} />
                                }
                              </IconButton>
                            </Tooltip>
                          }
                        </Stack>
                      )
                    }
                  </>
                )}
              </Stack>
            </CollapseField>

            {/* ----- 8th Expression Text Input Field ----- */}
            <CollapseField open={vocabExpressionShowCount > 7}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <PiChatCircleDots opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Expression"
                  multiline
                  rows={2}
                  onChange={(e) => setVocabExpressionEight(e.target.value)}
                  value={vocabExpressionEight}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "80%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={clearExpressionEight}>
                  <IoMdClose size={16} />
                </IconButton>
                {generatedAiExp && (
                  <>
                    {(vocabExpressionEight?.length < 1 && generatingExp) ?
                      (
                        <HashLoader
                          color={theme.palette.primary.main}
                          loading={true}
                          size={16}
                          aria-label="Loading Spinner"
                          data-testid="loader"
                        />
                      )
                      :
                      (
                        <Stack
                          onMouseOver={() => setShowRegenExpEight(true)}
                          onMouseLeave={() => setShowRegenExpEight(false)}
                        >
                          {(showRegenExpEight && vocabExpressionEight?.length > 1) ?
                            <Tooltip title="Regenerate" placement="right">
                              <IconButton onClick={genExpEight}>
                                <IoRefresh size={16} />
                              </IconButton>
                            </Tooltip>
                            :
                            <Tooltip title="Generate" placement="right">
                              <IconButton onClick={genExpEight}>
                                {vocabExpressionEight?.length < 1 ?
                                  <PiSparkle size={16} />
                                  :
                                  <PiSparkleFill size={16} />
                                }
                              </IconButton>
                            </Tooltip>
                          }
                        </Stack>
                      )
                    }
                  </>
                )}
              </Stack>
            </CollapseField>


            {/* ----- Sentence Text Input Field ----- */}
            <Stack direction={"row"} alignItems={"center"} spacing={1}>
              <Stack pr={1}>
                <PiChatCircleText size={20} color={theme.palette.neutral.darker} />
              </Stack>
              <InputBase
                id={uuidv4()}
                placeholder="Sentence"
                multiline
                rows={2}
                onChange={(e) => setVocabSentenceOne(e.target.value)}
                value={vocabSentenceOne}
                required={true}
                sx={{
                  fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                  width: isLandscape ? "80%" : "70%",
                  color: theme.palette.neutral.dark,
                  border: `solid 1px ${theme.palette.neutral.light}`,
                  borderRadius: "0.5rem",
                  padding: "0.25rem 0.5rem",
                  margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                }}
              />
              <IconButton onClick={incrementVocabSentenceCount}>
                <IoMdAdd size={16} />
              </IconButton>
            </Stack>


            {/* ----- 2nd Sentence Text Input Field ----- */}
            <CollapseField open={vocabSentenceShowCount > 1}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <PiChatCircleText opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Sentence"
                  multiline
                  rows={2}
                  onChange={(e) => setVocabSentenceTwo(e.target.value)}
                  value={vocabSentenceTwo}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "75%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={() => setVocabSentenceShowCount(vocabSentenceShowCount - 1)}>
                  <IoMdClose size={16} />
                </IconButton>
              </Stack>
            </CollapseField>

            {/* ----- 3rd Sentence Text Input Field ----- */}
            <CollapseField open={vocabSentenceShowCount > 2}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <PiChatCircleText opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Sentence"
                  multiline
                  rows={2}
                  onChange={(e) => setVocabSentenceThree(e.target.value)}
                  value={vocabSentenceThree}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "75%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={() => setVocabSentenceShowCount(vocabSentenceShowCount - 1)}>
                  <IoMdClose size={16} />
                </IconButton>
              </Stack>
            </CollapseField>

            {/* ----- 4th Sentence Text Input Field ----- */}
            <CollapseField open={vocabSentenceShowCount > 3}>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Stack pr={1}>
                  <PiChatCircleText opacity={0} size={20} color={theme.palette.neutral.darker} />
                </Stack>
                <InputBase
                  id={uuidv4()}
                  placeholder="Sentence"
                  multiline
                  rows={2}
                  onChange={(e) => setVocabSentenceFour(e.target.value)}
                  value={vocabSentenceFour}
                  required={true}
                  sx={{
                    fontSize: isWideScreens ? "1.25rem" : isQHDScreens ? "1rem" : "0.9rem",
                    width: isLandscape ? "75%" : "70%",
                    color: theme.palette.neutral.dark,
                    border: `solid 1px ${theme.palette.neutral.light}`,
                    borderRadius: "0.5rem",
                    padding: "0.25rem 0.5rem",
                    margin: !isNonMobileScreens ? "0 0.5rem" : isFullHDScreens ? "1rem 2rem" : "1rem 4rem"
                  }}
                />
                <IconButton onClick={() => setVocabSentenceShowCount(vocabSentenceShowCount - 1)}>
                  <IoMdClose size={16} />
                </IconButton>
              </Stack>
            </CollapseField>

            <Box margin={2} display={"flex"} justifyContent={"center"}>
              <Button
                onClick={handleCreateVocab}
                sx={{
                  padding: "1rem 4rem",
                  borderRadius: "6rem",
                  fontWeight: 600,
                  color: theme.palette.background.alt,
                  borderColor: theme.palette.neutral.dark,
                  backgroundColor: theme.palette.neutral.dark,
                  "&:hover": {
                    color: theme.palette.neutral.lighter,
                    backgroundColor: theme.palette.primary.main
                  }
                }}
              >
                Create
              </Button>
            </Box>
          </Stack>

        </Box>
      </Dialog>

      {/* ----- Snackbar on Guest Styles Limit Reached ----- */}
      <div>
        <Snackbar
          sx={{ height: "auto", zIndex: 10000 }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          open={openSnackbar}
          autoHideDuration={2000}
          onClose={handleSnackbarClose}
          message="Vocabulary Created!"
          action={createAction}
        />
      </div>
    </>
  );
}
